// External Library Imports
import React, { useEffect, useMemo, memo, useState } from "react";
import { Link, navigate } from "gatsby";
import queryString from "query-string";

// React-Bootstrap Components
import Badge from "react-bootstrap/Badge";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";

// Custom Hooks
import { useAppSelector } from "../../../hooks";
import { useHotelRates } from "../../../hooks/useHotelRates";
import { useHotels } from "../../../hooks/useHotels";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { useUnlockDiscount } from "../../../hooks/useUnlockDiscount";
import { useWebFramed } from "../../../hooks/useWebFramed";

// Redux State
import { searchSelector } from "../../../redux/slices/Search/search";
import { selectHotelRates } from "../../../redux/slices/Rate/rate";

// Utilities and Helpers
import { getLoggedStatus } from "../../../utils/helpers";
import { decodeHtmlEntities, truncateText } from "./HotelCardHelpers";

// Local Components and Styles
import HotelCardPrice from "../HotelCardPrice/HotelCardPrice";
import {
  BrandLogo,
  Card,
  HotelCarousel,
  ImagePriceContainer,
  Title4,
} from "./HotelCardHelpers";
import { hotelCardAmenitesWrapper } from "./HotelCard.module.scss";

// Static Assets
import fallbackImage from "../../../images/hotel_fallback.jpg";
import { TP_REDEMPTION_RATE } from "../../../@types/Constants";

// TypeScript Interfaces
import { IHotelCardProps } from "./HotelCardProps";

const CURRENT_LOCATION = "my current location";
const FROM_ME = "from me";
const baseUrl = process.env.GATSBY_CORE_CONTENT_BASE_URL;

const HotelCard = ({
  hotel,
  stacked = false,
  display = "list",
  positionInSearchResult,
  onRateLoad,
  isPromoValid,
}: IHotelCardProps) => {
  const isWebFramed = useWebFramed();
  const [isLoadingRates, selectedRoom] = useHotelRates(hotel, true);
  const [showOccupancy, setShowOccupancy] = useState(false);
  const hotels = useHotels();
  const search = useAppSelector(searchSelector);
  const isMobile = useMediaQuery("(max-width: 991px)");
  const priceFilter = search.priceFilter;
  const address = hotel.address;
  const addressParsed =
    typeof address === "string"
      ? address.replace(/(<([^>]+)>)/gi, "").split(", ")
      : `${address.streetAddress1},${address.locality},${address.region},${address.postalCode}`.split(
          ","
        );
  const addressUrl = addressParsed.join("+");
  const directionsLink = `https://www.google.com/maps/dir/?api=1&destination=${addressUrl}`;
  const isFindNearMe = search.searchString?.toLowerCase() === CURRENT_LOCATION;
  const loggedState = getLoggedStatus();
  const hotelRates = useAppSelector(
    selectHotelRates([hotel.crsHotelCode as string])
  );
  let isTPRNR;
  hotelRates[hotel.crsHotelCode]?.Rooms?.forEach((room: any) => {
    room?.roomRates?.forEach((rate: any) => {
      if (rate.rateCode && TP_REDEMPTION_RATE?.includes(rate.rateCode))
        isTPRNR = true;
    });
  });

  useEffect(() => {
    const filteredhotel = hotels.filter(
      (h: { crs_code: string }) => h.crs_code == hotel.crsHotelCode
    );

    if (
      filteredhotel &&
      filteredhotel.length &&
      filteredhotel[0]?.field_occupancy_messaging
    ) {
      setShowOccupancy(true);
    }
  }, [hotels, hotel.crsHotelCode]);

  const hotelRedemptionItem =
    hotelRates[hotel.crsHotelCode as string]?.redemptionItem;

  const getProductForDL = () => {
    const hotelPriceForDL = priceLocked
      ? selectedRoom
        ? selectedRoom?.BaseRateWithFees
        : null
      : hotel.bestPrice;
    const hotelRateForDL = priceLocked
      ? selectedRoom
        ? selectedRoom.BaseRateCode
        : null
      : hotel.bestRateCode;
    return {
      name: hotel.name,
      hotelId: hotel.crsHotelCode,
      list: search.searchString,
      category: hotelRateForDL,
      brand: hotel.brand?.name,
      price: hotelPriceForDL,
      position: positionInSearchResult,
    };
  };

  const fireProductClickEvnt = () => {
    const product = getProductForDL();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "productClick",
      ecommerce: {
        click: {
          actionField: { list: search.searchString },
          products: [product],
        },
      },
      loggedState: loggedState,
    });
  };
  const handleCheckAvailability = () => {
    fireProductClickEvnt();
    navigate(`${hotel.url}#rooms`, {
      state: {
        displayRoomCount: hotel.displayRoomCount,
      },
    });
  };

  useEffect(() => {
    if (isLoadingRates === false) {
      onRateLoad && onRateLoad();
    }
  }, [isLoadingRates]);

  const imageUrl =
    hotel.gallery && hotel.gallery.length
      ? decodeURI(
          hotel.gallery.indexOf("http") > -1
            ? hotel.gallery
            : baseUrl + hotel.gallery
        )
      : fallbackImage;

  const galleryImages = useMemo(
    () =>
      hotel?.galleryImages?.map((image) => ({
        imageUrl: decodeURI(
          image.url.indexOf("http") > -1 ? image.url : baseUrl + image.url
        ),
        altText: image.alt,
      })),
    [hotel?.galleryImages]
  );
  let priceClass = "no-price-filter";
  if (
    selectedRoom !== null &&
    priceFilter !== "undefined" &&
    priceFilter !== null
  ) {
    const rateForFilter = selectedRoom?.BaseRateWithFees
      ? Math.round(selectedRoom?.BaseRateWithFees)
      : Math.round(selectedRoom?.FromRateWithFees);
    if (priceFilter === "under_50" && rateForFilter >= 50) {
      priceClass = "filtered-by-price";
    } else if (
      priceFilter === "between_50_100" &&
      (rateForFilter < 50 || rateForFilter > 100)
    ) {
      priceClass = "filtered-by-price";
    } else if (priceFilter === "over_100" && rateForFilter <= 100) {
      priceClass = "filtered-by-price";
    }
  } else if (
    priceFilter !== "undefined" &&
    priceFilter !== null &&
    selectedRoom === null &&
    priceFilter !== "show_all"
  ) {
    priceClass = "filtered-by-price";
  }
  const [
    showUnlockButton,
    showMemberRateLabel,
    discountUnlocked,
    _showSpecialPricing,
    setShowSpecialPricing,
  ] = useUnlockDiscount(selectedRoom?.FromRateType);
  const priceLocked = showUnlockButton && !discountUnlocked; //selectedRoom?.FromRateType === 'member' && !isLoggedIn && !pricingUnlocked;

  const queryParams = queryString.parse(location.search, {
    parseBooleans: true,
    arrayFormat: "index",
  });

  const discountCode =
    queryParams?.ratePlanFilterCode ||
    search.discount ||
    search.groupCode ||
    search.promotionCode;
  const hotelName = hotel.name;

  const decodedTagline = hotel.tagline ? decodeHtmlEntities(hotel.tagline) : "";

  const allHotels = useHotels();
  const hotelCardAmenities = useMemo(() => {
    const matchingHotel = allHotels.find(
      (hotelInfo: any) => hotel.crsHotelCode === hotelInfo.crs_code
    );
    return matchingHotel?.hotelCardAmenities?.slice(0, 3);
  }, [allHotels, hotel.crsHotelCode]);
  return (
    <Card priceClass={priceClass} stacked={stacked}>
      <Col
        lg={stacked ? 12 : 4}
        className={`image-wrapper ${display === "map" && "gradient"}`}
      >
        {isMobile && (
          <HotelCarousel
            isMobile={true}
            galleryImages={
              galleryImages?.length > 0
                ? galleryImages
                : [{ imageUrl, hotelName }]
            }
          />
        )}
        {!isMobile && display != "map" && (
          <HotelCarousel
            isMobile={false}
            galleryImages={
              galleryImages?.length > 0
                ? galleryImages
                : [{ imageUrl, hotelName }]
            }
          />
        )}

        {display === "map" && !isMobile && (
          <HotelCarousel
            isMobile={false}
            display={display}
            galleryImages={
              galleryImages.length > 0
                ? galleryImages
                : [{ imageUrl, hotelName }]
            }
          />
        )}

        {hotel.tags && hotel.tags.length > 1 && (
          <Badge variant="primary" className="rounded-0 hotelTag">
            {hotel.tags}
          </Badge>
        )}
      </Col>

      <Col className={isMobile ? "h-100" : "h-100 d-flex"}>
        <Row className="h-100 px-0 pb-31 pt-0 pe-lg-0 g-0 content-inner-wrap">
          <Col className="d-flex flex-column pe-0 pe-sm-3 pe-lg-0 card-columm-container">
            <div
              itemScope={true}
              itemType="http://schema.org/PostalAddress"
              className={
                display !== "map"
                  ? "card-content-wrapper"
                  : "card-content-wrapper map"
              }
            >
              <div>
                <BrandLogo className="d-flex mx-3 mx-lg-0">
                  <img
                    src={hotel.brand.logoUrl}
                    alt={`${hotel.brand.name} Logo`}
                  />
                </BrandLogo>
              </div>
              <div
                className={`${
                  !isWebFramed && "d-table"
                } px-3 px-lg-0 name-container`}
              >
                <Title4 id={`hotel-name-in-search-${hotel.hotelCode}`}>
                  <Link
                    id={`hotel-path-link-${hotel.hotelCode}`}
                    to={hotel.url}
                    onClick={fireProductClickEvnt}
                  >
                    {hotel.name}
                  </Link>
                </Title4>
              </div>

              {!isMobile &&
                display != "map" &&
                hotel.tagline &&
                hotel.tagline != "false" &&
                hotel.tagline.length > 1 && (
                  <div className="px-0 d-lg-block mb-4 mb-lg-0">
                    <p className="tagline">
                      {truncateText(decodedTagline, 150)}
                    </p>
                  </div>
                )}

              <div className={`px-3 mb-lg-0 mt-lg-0 px-lg-0 d-lg-block`}>
                <Link
                  to={hotel.url}
                  className={
                    "view-property text-decoration-none align-self-sm-start"
                  }
                  onClick={fireProductClickEvnt}
                  id={"view-property-link"}
                >
                  View Hotel Details
                </Link>{" "}
              </div>
            </div>
            <div
              className={isMobile ? "px-3" : "d-sm-flex flex-sm-column"}
              style={{ gap: "16px" }}
            >
              <div className={isMobile ? "address-block" : "address-block"}>
                <div
                  className={
                    isMobile
                      ? "d-flex flex-column"
                      : "d-flex flex-wrap justify-content-start align-items-end"
                  }
                  style={isMobile ? { gap: "5px 4px" } : { gap: "8px 4px" }}
                >
                  <div className={isMobile ? "col-lg-6" : ""}>
                    <div
                      id="hotel-navigation-link"
                      className="directions me-1 d-flex"
                    >
                      <svg className="map-icon">
                        <use xlinkHref="/svg/mapview_hotelcard.svg#mapview_hotelcard"></use>
                      </svg>
                      <a
                        className="directionsLink"
                        href={directionsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        dangerouslySetInnerHTML={
                          typeof hotel.address === "string"
                            ? { __html: hotel.address }
                            : {
                                __html: `${address.cityName}, ${address.region}`,
                              }
                        }
                      />
                    </div>
                  </div>

                  <div className={isMobile ? "col-lg-6" : ""}>
                    <div className={isMobile ? "distance" : "distance"}>
                      <span className="me-1">
                        {hotel.rangeInMilesFromOrigin < 1
                          ? `${hotel.rangeInMilesFromOrigin.toFixed(1)}`
                          : `${Math.round(hotel.rangeInMilesFromOrigin)}`}{" "}
                        miles
                      </span>{" "}
                      <span className="from-destination-text">
                        from destination
                      </span>{" "}
                      <span className="px-1">{isFindNearMe && FROM_ME}</span>
                    </div>
                  </div>
                </div>
              </div>
              {(isMobile || display === "map") && (
                <div className="movile-divider"></div>
              )}
              {!isMobile && display !== "map" && hotelCardAmenities?.length ? (
                <div className={hotelCardAmenitesWrapper}>
                  <div className="d-flex align-items-center">
                    {hotelCardAmenities.map((amenity: any, index: number) => {
                      return (
                        <React.Fragment key={amenity.name}>
                          {index > 0 && (
                            <span className="mx-2 amenity-separator">•</span>
                          )}
                          <span>{amenity.name}</span>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          </Col>
          <Col
            sm="12"
            lg={display === "map" ? 12 : 3}
            className={
              isMobile
                ? `d-sm-block`
                : display === "map"
                ? "d-lg-block"
                : "d-lg-none"
            }
          >
            <ImagePriceContainer
              class={`${
                display === "map" ? "d-lg-block" : "d-lg-none"
              } d-block px-3 px-lg-0`}
            >
              {!isLoadingRates ? (
                <HotelCardPrice
                  discountCode={discountCode}
                  phone={hotel.phone}
                  price={hotel.bestPrice}
                  baseRate={
                    selectedRoom ? selectedRoom?.BaseRateWithFees : null
                  }
                  locked={priceLocked}
                  baseRateFees={
                    selectedRoom ? selectedRoom?.BaseRateFees : null
                  }
                  feeItems={selectedRoom ? selectedRoom?.FeeItems : null}
                  fromRateFeeItems={
                    selectedRoom ? selectedRoom?.FromRateFeeItems : null
                  }
                  fromRateFees={
                    selectedRoom ? selectedRoom?.FromRateFees : null
                  }
                  hotel={hotel}
                  display={display}
                  showUnlockButton={showUnlockButton}
                  handleCheckAvailability={handleCheckAvailability}
                  hotelRedemptionItem={isTPRNR ? hotelRedemptionItem : {}}
                  showOccupancy={showOccupancy}
                  showMemberRateLabel={showMemberRateLabel}
                  isPromoValid={isPromoValid}
                />
              ) : null}
            </ImagePriceContainer>
          </Col>
          {!isMobile && display !== "map" ? (
            <Col className="d-sm-flex flex-sm-column align-items-sm-end justify-content-sm-center col-lg-1 pricing-divider-column">
              <div className="pricing-divider"></div>
            </Col>
          ) : null}

          <Col
            lg={stacked ? 12 : 3}
            className={`d-sm-flex flex-sm-column pricing-wrapper ${
              !hotel.bestPrice || isLoadingRates
                ? "justify-content-sm-center"
                : "justify-content-sm-end"
            }`}
          >
            {isLoadingRates ? (
              <Spinner animation="border" />
            ) : (
              <>
                <div
                  className={`${
                    display === "list" ? "d-lg-block" : "d-lg-none"
                  } d-none`}
                >
                  <HotelCardPrice
                    discountCode={discountCode}
                    phone={hotel.phone}
                    price={hotel.bestPrice}
                    baseRate={
                      selectedRoom ? selectedRoom?.BaseRateWithFees : null
                    }
                    baseRateFees={
                      selectedRoom ? selectedRoom?.BaseRateFees : null
                    }
                    feeItems={selectedRoom ? selectedRoom?.FeeItems : null}
                    fromRateFeeItems={
                      selectedRoom ? selectedRoom?.FromRateFeeItems : null
                    }
                    fromRateFees={
                      selectedRoom ? selectedRoom?.FromRateFees : null
                    }
                    locked={priceLocked}
                    hotel={hotel}
                    display={display}
                    showUnlockButton={showUnlockButton}
                    handleCheckAvailability={handleCheckAvailability}
                    hotelRedemptionItem={isTPRNR ? hotelRedemptionItem : {}}
                    showOccupancy={showOccupancy}
                    showMemberRateLabel={showMemberRateLabel}
                    isPromoValid={isPromoValid}
                  />
                </div>
              </>
            )}
          </Col>
        </Row>
      </Col>
    </Card>
  );
};
HotelCard.whyDidYouRender = true;
export default memo(HotelCard);
